import { create } from "zustand";

import type { AssetListItem, AssetStatus, FolderItem, ViewerKind } from "@/lib/api";
import { reconcileList } from "@/lib/listReconcile";

interface Filters {
  status: AssetStatus | null;
  /** Qual viewer o arquivo abre — `document` ou `packaging`; null = todos. */
  viewerKind: ViewerKind | null;
  hasNotes: boolean | null;
  search: string;
}

export type FileManagerViewMode = "grid" | "list";
export type FileManagerSortOrder = "uploaded" | "name";

interface FileManagerState {
  currentFolder: string;
  subfolders: FolderItem[];
  assets: AssetListItem[];
  selectedAssetId: string | null;
  selectedNoteId: string | null;
  currentNotesPage: number;
  filters: Filters;
  viewMode: FileManagerViewMode;
  sortOrder: FileManagerSortOrder;
  /** Página atual (0-based) da listagem de assets — Fase 5 F6. */
  page: number;
  pageSize: number;
  /** Total de assets do filtro atual; null quando não é exato (filtro de status). */
  totalAssets: number | null;
  loading: boolean;
  error: string | null;

  setCurrentFolder: (folder: string) => void;
  setSubfolders: (subfolders: FolderItem[]) => void;
  setAssets: (assets: AssetListItem[]) => void;
  updateAsset: (assetId: string, patch: Partial<AssetListItem>) => void;
  setSelectedAssetId: (id: string | null) => void;
  setSelectedNoteId: (id: string | null) => void;
  setCurrentNotesPage: (page: number) => void;
  setFilters: (filters: Partial<Filters>) => void;
  setViewMode: (viewMode: FileManagerViewMode) => void;
  setSortOrder: (sortOrder: FileManagerSortOrder) => void;
  setPage: (page: number) => void;
  setTotalAssets: (total: number | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useFileManagerStore = create<FileManagerState>((set) => ({
  currentFolder: "",
  subfolders: [],
  assets: [],
  selectedAssetId: null,
  selectedNoteId: null,
  currentNotesPage: 1,
  filters: { status: null, viewerKind: null, hasNotes: null, search: "" },
  viewMode: "grid",
  sortOrder: "uploaded",
  page: 0,
  pageSize: 100,
  totalAssets: null,
  loading: false,
  error: null,

  setCurrentFolder: (currentFolder) =>
    set({
      currentFolder,
      selectedAssetId: null,
      selectedNoteId: null,
      currentNotesPage: 1,
      page: 0,
    }),
  // O refresh de fundo traz a lista inteira de novo. Reaproveitar os objetos
  // que não mudaram — e o próprio array, quando nada mudou — é o que deixa o
  // `memo` dos cards poupar o re-render de milhares de pastas a cada ciclo.
  // Devolver `state` sem mudança é um no-op para o zustand: ninguém acorda.
  setSubfolders: (subfolders) =>
    set((state) => {
      const next = reconcileList(state.subfolders, subfolders, (folder) => folder.path);
      return next === state.subfolders ? state : { subfolders: next };
    }),
  setAssets: (assets) =>
    set((state) => {
      const next = reconcileList(state.assets, assets, (asset) => asset.id);
      return next === state.assets ? state : { assets: next };
    }),
  updateAsset: (assetId, patch) =>
    set((state) => ({
      assets: state.assets.map((asset) =>
        asset.id === assetId ? { ...asset, ...patch } : asset,
      ),
    })),
  setSelectedAssetId: (selectedAssetId) =>
    set({ selectedAssetId, selectedNoteId: null, currentNotesPage: 1 }),
  setSelectedNoteId: (selectedNoteId) => set({ selectedNoteId }),
  setCurrentNotesPage: (currentNotesPage) =>
    set({ currentNotesPage, selectedNoteId: null }),
  setFilters: (filters) =>
    set((state) => ({ filters: { ...state.filters, ...filters }, page: 0 })),
  setViewMode: (viewMode) => set({ viewMode }),
  setSortOrder: (sortOrder) => set({ sortOrder, page: 0 }),
  setPage: (page) => set({ page: Math.max(0, page) }),
  setTotalAssets: (totalAssets) => set({ totalAssets }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
}));
